import { prefetch, requireData, storedLocation, locationKey } from './routeData';
import { eventService, applicationService, adminService, featuredWallService, squareService, signalService, peopleService, clubService, proService, calendarService } from '../services';
import mimeService from '../services/mimeService';

export const eventKey = id => `event:${id}`;
export const wallKey = category => `wall:${category || ''}:${locationKey()}`;
export const squareKey = slug => `square:${slug}`;
export const peopleKey = () => `people:${locationKey()}`;
export const clubsKey = (tab, q = '') => `clubs:${tab}:${q}`;
export const calendarKey = date => `calendar:${date.getFullYear()}:${date.getMonth()}:${locationKey()}`;

export function loadEvent(id, options) {
  // Either signal means "ask for the viewer's application": the context's flag, or the stored
  // token — on a hard refresh the token is there before the flag is (EventApplyPage explains).
  const authenticated = !!options?.authenticated || !!localStorage.getItem('accessToken');
  return prefetch(eventKey(id), async () => {
    // A canonical URL arrives as "city/slug"; an invite code or id never contains a slash, so the
    // separator alone tells the two apart and the cache key stays one string either way.
    const slash = typeof id === 'string' ? id.indexOf('/') : -1;
    let r;
    if (slash > 0) {
      r = await eventService.getEventBySlug(id.slice(0, slash), id.slice(slash + 1));
    } else {
      r = await eventService.getEventByCode(id);
      if (!r?.success) r = await eventService.getEvent(id);
    }
    const event = requireData(r);
    const [flags, app, mime] = await Promise.all([
      adminService.getFeatureFlags(),
      authenticated ? applicationService.getMyApplicationForEvent(event.id) : null,
      authenticated ? mimeService.getMyRequestsForEvent(event.id) : null,
    ]);
    return { event, application: app?.data || null, mime: mime?.data || [], golden: flags?.data?.goldenTicketsEnabled !== false };
  }, options);
}
export function loadWall(category, options, location = storedLocation()) {
  const { lat = null, lng = null } = location;
  return prefetch(wallKey(category), async () => requireData(await featuredWallService.getWall({ category: category || null, lat, lng })), options);
}
export function loadSquare(slug, options) {
  return prefetch(squareKey(slug), async () => {
    const [square, feed, town] = await Promise.all([squareService.getSquare(slug), squareService.getFeed(slug), squareService.getTown(slug)]);
    return { square: requireData(square), feed: requireData(feed) || [], town: requireData(town) };
  }, options);
}
export function loadPeople(options) {
  const { lat = null, lng = null } = storedLocation();
  return prefetch(peopleKey(), async () => {
    const [signals, activity] = await Promise.all([signalService.getFeed({ lat, lng }), peopleService.getActivity({ lat, lng })]);
    return { signals: requireData(signals) || [], activity: requireData(activity) || [] };
  }, options);
}
export function loadClubs(tab, q = '', options) {
  return prefetch(clubsKey(tab, q), async () => requireData(await (tab === 'discover' ? clubService.getPublicClubs(q) : clubService.getMyClubs())), options);
}
export function loadCalendar(cursor = new Date(), options, location = storedLocation()) {
  const { lat, lng } = location;
  return prefetch(calendarKey(cursor), async () => requireData(await calendarService.getCalendar({
    lat, lng, radiusMi: 50,
    from: new Date(cursor.getFullYear(), cursor.getMonth(), 1).toISOString(),
    to: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59).toISOString(),
  })), options);
}
export const loadPro = (slug, options) => prefetch(`pro:${slug}`, async () => requireData(await proService.getBySlug(slug)), options);

// Resolve redirects BEFORE committing the route, so the destination doesn't change mid-flight.
export async function prepareRoute(to) {
  const url = new URL(typeof to === 'string' ? to : `${to.pathname || window.location.pathname}${to.search || ''}${to.hash || ''}`, window.location.href);
  let p = url.pathname;
  if (p === '/square' && localStorage.getItem('accessToken')) {
    const loc = storedLocation();
    const square = requireData(await squareService.resolve(loc));
    if (square?.slug) { p = `/square/${square.slug}`; url.pathname = p; }
  }
  const event = p.match(/^\/(?:events|e)\/([^/]+)$/);
  const square = p.match(/^\/square\/([^/]+)$/);
  const pro = p.match(/^\/pro\/([^/]+)$/);
  if (p === '/featured') await loadWall(url.searchParams.get('category'));
  else if (p === '/people' && localStorage.getItem('accessToken')) await loadPeople();
  else if (p === '/clubs' && localStorage.getItem('accessToken')) await loadClubs(url.searchParams.get('tab') === 'discover' ? 'discover' : 'my', url.searchParams.get('q') || '');
  else if (p === '/calendar' && localStorage.getItem('accessToken')) await loadCalendar();
  else if (square && localStorage.getItem('accessToken')) {
    const data = await loadSquare(decodeURIComponent(square[1]));
    if (data.square.slug && data.square.slug !== square[1]) {
      url.pathname = `/square/${data.square.slug}`;
      await loadSquare(data.square.slug);
    }
  } else if (event && !['all', 'create'].includes(event[1])) await loadEvent(decodeURIComponent(event[1]));
  else if (pro && !['setup', 'dashboard', 'desk', 'flags'].includes(pro[1])) await loadPro(decodeURIComponent(pro[1]));
  return `${url.pathname}${url.search}${url.hash}`;
}
